import React, { useState } from "react";
import { useLocation } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Sparkles, X, Send, ShieldAlert, CheckCircle2 } from "lucide-react";

const SUGGESTIONS = {
  "/admin/contacts": "Show everyone who started applying this week but did not pay",
  "/admin/crm": "Tag all Africa applicants without payment as 'recover'",
  "/admin/campaigns": "Create a 14-day nurture sequence for Cameroon K-12 parents",
  "/admin/inbox": "Draft responses to all unanswered admissions emails",
  "/admin/autopilot": "Give me a short executive summary of the business right now",
};

export function SimoAI() {
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [res, setRes] = useState(null);
  if (!loc.pathname.startsWith("/admin")) return null;

  const run = async (p) => {
    const text = p || prompt;
    if (!text.trim()) return;
    setBusy(true); setRes(null);
    try {
      const r = await api.post("/admin/ai/act", { prompt: text, page: loc.pathname });
      setRes(r.data);
      if (r.data.status === "awaiting_approval") toast.warning("High-risk action queued for your approval");
      else if (r.data.status === "executed") toast.success("Done");
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    setBusy(false);
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="simoai-launcher"
          className="fixed bottom-6 right-6 z-40 inline-flex items-center gap-2 bg-heraldic text-white px-4 py-3 rounded-full shadow-lg hover:opacity-90 transition-opacity">
          <Sparkles className="h-5 w-5" /> <span className="text-sm font-medium">Ask Simo AI</span>
        </button>
      )}
      {open && (
        <div className="fixed bottom-6 right-6 z-40 w-[380px] max-w-[92vw] bg-[#0B0D12] border border-white/15 rounded-lg shadow-2xl text-paper" data-testid="simoai-panel">
          <div className="flex items-center justify-between px-4 h-12 border-b border-white/10">
            <span className="text-sm font-medium flex items-center gap-2"><Sparkles className="h-4 w-4 text-heraldic" /> Simo AI · Autopilot</span>
            <button onClick={() => setOpen(false)} data-testid="simoai-close"><X className="h-4 w-4 text-paper/50" /></button>
          </div>
          <div className="p-4 max-h-[52vh] overflow-auto">
            <div className="text-xs text-paper/40 mb-2">On this page you can say things like:</div>
            <button onClick={() => setPrompt(SUGGESTIONS[loc.pathname] || "Summarise what I'm looking at")} className="text-xs text-heraldic text-left mb-3">“{SUGGESTIONS[loc.pathname] || "Summarise what I'm looking at"}”</button>

            {res && (
              <div className="mb-3 border border-white/10 rounded-sm p-3 bg-white/5" data-testid="simoai-result">
                {res.status === "awaiting_approval" && <div className="text-amber-300 text-sm flex items-center gap-2"><ShieldAlert className="h-4 w-4" /> {res.message} <span className="text-paper/50">(see Autopilot → Approvals)</span></div>}
                {res.status === "unclear" && <div className="text-paper/60 text-sm">{res.message}</div>}
                {res.status === "executed" && (
                  <div className="text-sm">
                    <div className="text-emerald-300 flex items-center gap-1.5 mb-1"><CheckCircle2 className="h-4 w-4" /> {res.reasoning || "Done"}</div>
                    <Result r={res.result} onSend={run} />
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="p-3 border-t border-white/10 flex gap-2">
            <input value={prompt} onChange={(e) => setPrompt(e.target.value)} onKeyDown={(e) => e.key === "Enter" && run()} placeholder="Tell Simo what to do…" data-testid="simoai-input" className="flex-1 bg-black/30 border border-white/10 rounded-sm px-3 py-2 text-sm" />
            <button onClick={() => run()} disabled={busy} data-testid="simoai-send" className="bg-heraldic text-white px-3 rounded-sm disabled:opacity-50">{busy ? "…" : <Send className="h-4 w-4" />}</button>
          </div>
        </div>
      )}
    </>
  );
}

function Result({ r }) {
  if (!r) return null;
  if (r.kind === "text" || r.kind === "error") return <div className="text-paper/80 whitespace-pre-line">{r.title ? <b className="block mb-1">{r.title}</b> : null}{r.text}</div>;
  if (r.kind === "done" || r.kind === "created") return <div className="text-paper/80">{r.title}{r.link && <a href={r.link} className="text-heraldic ml-2">Open →</a>}</div>;
  if (r.kind === "list") return (
    <div><b className="block mb-1">{r.title}</b><div className="space-y-1 max-h-40 overflow-auto">{(r.rows || []).map((row, i) => <div key={i} className="text-xs text-paper/60 border-b border-white/5 pb-1">{Object.values(row).filter(Boolean).slice(0, 3).join(" · ")}</div>)}</div></div>
  );
  if (r.kind === "drafts") return (
    <div><b className="block mb-1">{r.title}</b><div className="space-y-2">{(r.rows || []).map((d, i) => <div key={i} className="text-xs bg-black/30 rounded-sm p-2"><div className="text-paper/40">→ {d.to}: {d.incoming}</div><div className="text-paper/80 mt-1">{d.draft}</div></div>)}</div></div>
  );
  return <div className="text-paper/70 text-xs">{JSON.stringify(r)}</div>;
}
